import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { ParticipantRole } from '@/consts/meeting';
import { useLocation } from 'react-router';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Skeleton } from 'primereact/skeleton';

import './create_meeting.scss';
import clsx from 'clsx';
import Api from '@/api/api.ts';
import { useNavigate } from 'react-router';
import { useMeetingStore } from '@/stores/meeting.ts';
import { showToast } from '@/components/ToastManager';

function CreateMeeting() {
  const navigate = useNavigate();
  const [visibleMaterialsCard, setVisibleMaterialsCard] = useState<boolean>(false);
  const { resetMeeting, resetDefaultRooms } = useMeetingStore.getState();
  const { state } = useLocation();

  const [userName, setUserName] = useState('');
  const [meetingName, setMeetingName] = useState('');
  const [downloadUrl, setDownloadUrl] = useState<string | null | undefined>(undefined);

  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);

  const validateForm = () => {
    const isInvalid = !userName;
    setValidationError(isInvalid);
    return !isInvalid;
  };

  const createMeeting = async () => {
    if (!validateForm()) {
      showToast('warningToast', 'warn', 'Введите имя, чтобы создать встречу');
      return;
    }

    setIsLoading(true);
    try {
      const authorUuid = localStorage.getItem('userId') || crypto.randomUUID();
      const params = { title: meetingName, author_uuid: authorUuid };
      const newMeeting = await Api.createMeeting(params);

      if (!newMeeting?.uuid) {
        throw new Error('Meeting creation failed, no UUID returned.');
      }

      navigate(`/meeting/${newMeeting.uuid}`, {
        state: { author_name: userName, author_uuid: authorUuid }
      });
    } catch (error) {
      console.error('Error creating meeting:', error);
      showToast(
        'errorToast',
        'error',
        'Ошибка при создании встречи',
        <>
          Обратитесь к администратору{' '}
          <a
            href="https://t.me/repeteka_support"
            target="_blank"
            style={{ color: '#0ea5e9' }}
            rel="noreferrer"
          >
            @repeteka_support
          </a>
        </>
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!state?.finished_meeting_uid || !state?.user_uuid) return;

    const fetchZip = async () => {
      try {
        setFinishLoading(true);
        const response = await Api.getZipWithBoards(state?.finished_meeting_uid, state?.user_uuid);
        if (!response) {
          setDownloadUrl(null);
          return;
        }
        const url = window.URL.createObjectURL(response.data);
        setDownloadUrl(url);
      } catch (err) {
        console.error('Ошибка при загрузке архива с досками:', err);
      } finally {
        setFinishLoading(false);
      }
    };

    fetchZip();

    return () => {
      if (downloadUrl) {
        window.URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [state?.finished_meeting_uid, state?.user_uuid]);

  useEffect(() => {
    if (
      state?.finished_meeting_uid &&
      state?.role &&
      state?.user_uuid &&
      state?.finished_meeting_name
    ) {
      setVisibleMaterialsCard(true);
      if (downloadUrl === undefined) {
        setFinishLoading(true);
      }
    } else return;
  }, [
    state?.finished_meeting_uid,
    state?.user_uuid,
    state?.role,
    state?.finished_meeting_name,
    downloadUrl
  ]);

  useEffect(() => {
    resetMeeting();
    resetDefaultRooms();
  }, []);

  return (
    <>
      <div className={clsx('card-after-meeting__foreground', !finishLoading && 'loading_finish')}>
        <div className="card-after-meeting__loader">
          <ProgressSpinner className="card-after-meeting__custom-spinner" />
          <p>⚙️ Сохраняем материалы с занятия, пожалуйста подождите...</p>
        </div>
      </div>
      <Dialog
        header="Встреча завершена"
        visible={visibleMaterialsCard}
        draggable={false}
        closable={!finishLoading}
        onHide={() => {
          if (!visibleMaterialsCard) return;
          setVisibleMaterialsCard(false);
        }}
        className="card-after-meeting"
      >
        <div>
          Материалы по встрече можно найти по ссылке <br />
          <br />
          {finishLoading ? (
            <Skeleton
              width="50%"
              height="3.5rem"
              className="card-after-meeting__skeleton_materials"
            ></Skeleton>
          ) : (
            <a
              href={`${window.location.origin}/meeting/${state?.finished_meeting_uid}/materials`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-button"
            >
              <i className="pi pi-link" />
              Ссылка на материалы
            </a>
          )}
          {state?.role === ParticipantRole.STUDENT && (
            <>
              {!finishLoading && (
                <>
                  <br />
                  <br />
                </>
              )}
              {downloadUrl || finishLoading ? (
                <>
                  Скачать ваши интерактивные доски можно по кнопке <br />
                </>
              ) : (
                <>Вы ничего не записывали на досках</>
              )}
              <br />
              {finishLoading ? (
                <Skeleton
                  width="30%"
                  height="3.5rem"
                  className="card-after-meeting__skeleton_materials"
                ></Skeleton>
              ) : (
                downloadUrl && (
                  <a
                    href={downloadUrl}
                    download={`${state?.finished_meeting_name}_boards.zip`}
                    className={clsx('p-button')}
                  >
                    <>
                      <i className="pi pi-download" />
                      Скачать мои доски
                    </>
                  </a>
                )
              )}
            </>
          )}
        </div>
      </Dialog>
      <div className="new-meeting">
        <Card className="new-meeting__card" title="Добро пожаловать в Repeteka!">
          <div className="new-meeting__form-item">
            <label className="new-meeting__label required" htmlFor="name">
              Ваше имя
            </label>
            <InputText
              className={clsx('p-inputtext-sm', validationError && !userName && 'form-item_error')}
              id="name"
              placeholder="Имя отображается во время встречи"
              value={userName}
              required
              onChange={(e) => setUserName(e.target.value)}
            />
          </div>
          <div className="new-meeting__form-item">
            <label className="new-meeting__label" htmlFor="meetingName">
              Название встречи
            </label>
            <InputText
              className="p-inputtext-sm"
              id="meetingName"
              value={meetingName}
              onChange={(e) => setMeetingName(e.target.value)}
            />
          </div>
          <Button
            className="new-meeting__create-button"
            size="small"
            onClick={createMeeting}
            loading={isLoading}
          >
            Создать
          </Button>
        </Card>
      </div>
    </>
  );
}

export default CreateMeeting;
